import { ensureMapObjectPane } from '../map-object-focus';
import * as L from 'leaflet';
import { map } from 'rxjs';

import { GeometryFeatureBase } from './geometry-feature.base';
import { areaRingsIntersectPath, boundsPath } from '../spatial/area-intersection';
import { MapObject, MapObjectId } from '../map-object';
import { ObjectSelection } from '../object-selection';
import {
  MapObjectSelectionHandler,
  SelectableFeature,
} from '../selectable-feature';

export interface AreaMapObject extends MapObject<number> {
  readonly areaId: number;
  readonly boundaryGeoJson: string;
}

/** Shared polygon selection for tenant Areas and public ZIP boundaries. */
export abstract class AreaFeatureBase<TObject extends MapObject>
extends GeometryFeatureBase<TObject>
implements SelectableFeature<TObject> {
  readonly geometryType = 'area' as const;
  readonly selection = new ObjectSelection<TObject['id']>();
  get selectedObjectIds(): readonly TObject['id'][] { return this.selection.ids; }
  readonly selectionCount$ = this.selection.changed$.pipe(
    map(() => this.selection.size),
  );
  protected selectionEnabled = false;
  /** The same rendered shapes used by the feature, not a second geometry copy. */
  protected readonly shapesByObjectId = new Map<TObject['id'], L.GeoJSON>();
  private objectSelectionHandler: MapObjectSelectionHandler | null = null;

  enableSelection(): void {
    this.selectionEnabled = true;
  }

  disableSelection(): void {
    this.selectionEnabled = false;
  }

  setObjectSelectionHandler(handler: MapObjectSelectionHandler | null): void {
    this.objectSelectionHandler = handler;
  }

  protected selectObject(
    objectId: TObject['id'],
    originalEvent?: MouseEvent,
  ): ReturnType<MapObjectSelectionHandler> {
    if (!this.selectionEnabled || !this.objectSelectionHandler) return false;
    return this.objectSelectionHandler([objectId], originalEvent);
  }

  isObjectSelected(id: string | number): boolean { return this.selection.has(id); }

  setObjectsSelected(ids: readonly (string | number)[], selected: boolean): void {
    if (selected) this.selection.append(ids);
    else this.selection.remove(ids);
  }

  selectObjectById(id: string | number): void {
    this.selection.append([id as TObject['id']]);
  }

  toggleObjectSelection(ids: readonly (string | number)[]): void {
    for (const objectId of this.objectIds(ids)) this.selection.toggle(objectId);
  }

  appendObjectSelection(ids: readonly (string | number)[]): void {
    this.selection.append(this.objectIds(ids));
  }

  selectInBounds(bounds: L.LatLngBounds): void {
    this.selection.append(this.selectionInBounds(bounds));
  }

  clearSelection(): void {
    this.selection.clear();
  }

  selectionInBounds(bounds: L.LatLngBounds): TObject['id'][] {
    return this.selectionInPath(boundsPath(bounds));
  }

  selectionInPath(path: readonly L.LatLng[]): TObject['id'][] {
    if (!this.enabled) return [];
    const ids: TObject['id'][] = [];
    for (const [objectId, shape] of this.shapesByObjectId) {
      let hit = false;
      shape.eachLayer(layer => {
        if (hit || !(layer instanceof L.Polygon) || !layer.getElement()?.isConnected) return;
        const rings = layer.getLatLngs() as L.LatLng[][] | L.LatLng[][][];
        const polygons = rings[0]?.[0] instanceof L.LatLng
          ? [rings as L.LatLng[][]] : rings as L.LatLng[][][];
        hit = areaRingsIntersectPath(polygons, path);
      });
      if (hit) ids.push(objectId);
    }
    return ids;
  }

  private readonly originalPanes = new WeakMap<L.Path, string>();

  protected useSelectionMap(map: L.Map): void {
    ensureMapObjectPane(map, 'map-object-selection', 620);
  }

  protected updateSelectedShapes(ids: readonly TObject['id'][]): void {
    for (const id of ids) {
      const shape = this.shapesByObjectId.get(id);
      if (!shape) continue;
      const selected = this.selection.has(id);
      shape.eachLayer(layer => {
        if (!(layer instanceof L.Path)) return;
        const originalPane = this.originalPanes.get(layer) ?? layer.options.pane ?? 'overlayPane';
        this.originalPanes.set(layer, originalPane);
        const pane = selected ? 'map-object-selection' : originalPane;
        if (layer.options.pane !== pane) {
          shape.removeLayer(layer);
          layer.options.pane = pane;
          layer.options.renderer = undefined;
          shape.addLayer(layer);
        }
        layer.options.className = `tr-pickable${selected ? ' map-area-selected' : ''}`;
        layer.getElement()?.classList.toggle('map-area-selected', selected);
      });
      if (selected) shape.bringToFront();
    }
  }

  private objectIds(ids: readonly MapObjectId[]): TObject['id'][] {
    const known = new Set(this.objects.map(object => object.id));
    return ids.filter(id => known.has(id)) as TObject['id'][];
  }
}
